import '@ohear/ui/styles'
// SLP Professional re-skin — must come AFTER the base styles so the token
// remap wins the cascade (redesign V2, approved 2026-06-21).
import './styles/slp-overrides.css'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import { init as initErrorCollector, installErrorReporter } from './lib/error-reporter'
import { initWebVitals } from './lib/web-vitals'

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('root element not found')

// Install global error handlers BEFORE rendering so an early-render exception
// gets reported. Pass the API base + the build's release version so reports
// can be pivoted by deploy in the admin triage view.
const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.ohear.ai'
const releaseVersion =
  (import.meta.env.VITE_RELEASE_VERSION as string | undefined) ??
  (import.meta.env.MODE as string | undefined) ??
  'dev'
installErrorReporter({ apiUrl, releaseVersion })

// Also wire the dedicated error-collector Worker (Postgres + Workers Analytics
// Engine). The two paths coexist; this one is the new direct-to-Worker pipe
// used by the ErrorBoundary going forward.
const workerErrorEndpoint = import.meta.env.VITE_ERROR_ENDPOINT as string | undefined
if (workerErrorEndpoint) {
  initErrorCollector(workerErrorEndpoint, () => ({
    route: typeof location !== 'undefined' ? location.pathname : undefined,
    release: releaseVersion,
  }))
}

createRoot(rootEl).render(
  <StrictMode>
    {/* The ErrorBoundary wraps the whole App (and therefore RouterProvider)
        so a render-phase exception in any route surfaces a friendly fallback
        rather than a blank page. */}
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)

// Production-only: Cloudflare Browser Insights + Web Vitals → /api/v1/telemetry/metric.
initWebVitals()
